import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  Button,
  InputBase,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import useDebounce from '../../../../hooks/useDebounce';
import useWindowDimensions from '../../../../hooks/useWindowDimensions';
import NoItemBox from '../../../../shared-components/NoItemBox';
import { ICoreUser } from '../../../../shared-interfaces/User/ICoreUser';
import UserService, {
  OrderUserSearch,
  SearchTypes,
} from '../../../../shared-services/User/UserService';
import { Role } from '../../../../shared-types/Roles';
import { StudentAlertOrderByTypes } from '../../Types/StudentAlertOrderByTypes';
import { FollowingType, TermType } from '../../Types/StudentFilterTypes';
import MetricFooter, { MetricFooterSkeleton } from '../MetricFooter';
import RadioImpersonatorIcon from '../RadioImpersonatorIcon';
import StudentFilterPopoverContent from './StudentFilterPopoverContent';
import StudentsListItem, { StudentsSkeleton } from './StudentsListItem';

const useStyles = makeStyles(theme => ({
  header: {
    fontSize: '1.25rem',
    fontWeight: 500,
    margin: '0',
  },
  headerToolbar: {
    marginLeft: 0,
    paddingLeft: '10px',
    width: '100%',
    backgroundColor: '#f9f9f9',
    minHeight: '52px',
  },
  toolbar: {
    position: 'sticky',
    top: '0px',
    zIndex: 100,
    backgroundColor: 'white',
    paddingBottom: '5px',
    height: '48px',
    minHeight: '48px',
    paddingLeft: 0,
    paddingRight: 0,
    marginLeft: 0,
    marginRight: 0,
    borderBottom: '1px solid #dcdcdc',
    borderTop: '1px solid #dcdcdc',
  },
  toolbarSearch: {
    outline: '1px solid #c4c4c4',
    outlineStyle: 'auto',
    outlineOffset: '-1px',
    paddingLeft: '8px',
    [theme.breakpoints.down('md')]: {
      width: '170px',
    },
  },
  toolbarItemContainer: {
    marginTop: '8px',
  },
  toolbarButton: {
    margin: '0 3px',
  },
  userSearch: {
    [theme.breakpoints.only('xs')]: {
      height: '20px',
      fontSize: '1.2rem',
    },
  },
  userSearchLabel: {
    [theme.breakpoints.only('xs')]: {
      fontSize: '1.2rem',
    },
  },
  menuItem: {
    height: '40px',
  },
}));

interface IStudents {
  showInNetworkOnly: boolean | undefined;
}

const Students = (props: IStudents) => {
  const PAGE_SIZE = 25;
  const theme = useTheme();
  const isLessThanSm = useMediaQuery(theme.breakpoints.down('md'));

  const [initialLoad, setInitialLoad] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [following, setFollowing] = useState<string[]>([]);

  const classes = useStyles();
  const { height } = useWindowDimensions();
  const { enqueueSnackbar } = useSnackbar();

  const [searchTerm, setSearchTerm] = useState<string>('');
  const debounceSearch = useDebounce(searchTerm, 500);

  const [orderBy, setOrderBy] = useState<StudentAlertOrderByTypes | undefined>(
    StudentAlertOrderByTypes.OpenAlerts,
  );

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [items, setItems] = useState<ICoreUser[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isExhausted, setIsExhausted] = useState(false);

  const [sortMenuAnchorEl, setSortMenuAnchorEl] = React.useState<
    (EventTarget & HTMLButtonElement) | null
  >(null);
  const openSortMenu = Boolean(sortMenuAnchorEl);
  const sortMenuId = openSortMenu ? 'alerts-over-time-filter' : undefined;
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState<
    (EventTarget & HTMLButtonElement) | null
  >(null);
  const openFilterMenu = Boolean(filterMenuAnchorEl);

  const [followingFilter, setFollowingFilter] = useState<FollowingType>('all');
  const [termFilter, setTermFilter] = useState<TermType>('all');

  useEffect(() => {
    setItems([]);
    setCurrentIndex(0);
    setIsExhausted(false);
  }, [debounceSearch]);

  useEffect(() => {
    if (props.showInNetworkOnly !== undefined) {
      setItems([]);
      setCurrentIndex(0);
      setIsExhausted(false);
    }
  }, [orderBy, props.showInNetworkOnly, followingFilter, termFilter]);

  useEffect(() => {
    if (
      props.showInNetworkOnly !== undefined &&
      !isExhausted &&
      items.length === 0 &&
      !initialLoad
    ) {
      setInitialLoad(true);
      UserService.getFollowingIds('', OrderUserSearch.OpenAlerts)
        .then(u => setFollowing(u))
        .then(() => {
          getter()
            .then(r => {
              setItems([...items, ...r]);
            })
            .catch(e => {})
            .finally(() => {
              setInitialLoad(false);
            });
        });
    }
  }, [isExhausted, items]);

  const getter = useCallback(async () => {
    function calculateOrderBy() {
      if (termFilter === 'all') {
        switch (orderBy) {
          case StudentAlertOrderByTypes.OpenAlerts:
            return OrderUserSearch.OpenAlerts;
          case StudentAlertOrderByTypes.TotalAlerts:
            return OrderUserSearch.TotalAlerts;
          default:
            return undefined;
        }
      } else if (termFilter === 'current') {
        switch (orderBy) {
          case StudentAlertOrderByTypes.OpenAlerts:
            return OrderUserSearch.CurrentTermOpenAlerts;
          case StudentAlertOrderByTypes.TotalAlerts:
            return OrderUserSearch.CurrentTermTotalAlerts;
          default:
            return undefined;
        }
      }
      return OrderUserSearch.OpenAlerts;
    }

    return UserService.search({
      inNetwork: props.showInNetworkOnly,
      take: PAGE_SIZE,
      skip: currentIndex,
      SearchType: SearchTypes.like,
      orderBy: calculateOrderBy(),
      term: searchTerm,
      RoleIds: [Role.Student, Role.PeerAdvisor],
      userIds: followingFilter === 'following' ? following : undefined,
      enrolledOnly: termFilter === 'current',
    }).then(x => {
      setCurrentIndex(x.skip + x.count);
      setTotalItems(x.totalCount);
      if (x.skip + x.count >= x.totalCount) {
        setIsExhausted(true);
      }
      return x.items;
    });
  }, [
    props.showInNetworkOnly,
    currentIndex,
    orderBy,
    searchTerm,
    followingFilter,
    termFilter,
  ]);

  return (
    <Paper elevation={2}>
      <Toolbar variant="regular" className={classes.headerToolbar}>
        <div style={{ width: '100%' }}>
          <Box display="flex">
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h2" className={classes.header}>
                Students
              </Typography>
            </Box>
          </Box>
        </div>
      </Toolbar>
      <Toolbar className={classes.toolbar}>
        <Box
          display="flex"
          style={{ width: '100%', paddingLeft: 0, paddingRight: 0 }}
        >
          <Box p={1} flexGrow={1} className={classes.toolbarItemContainer}>
            <InputBase
              id="search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              inputProps={{
                className: classes.userSearch,
                'aria-label': 'search',
              }}
              endAdornment={
                <SearchIcon style={{ color: '#c4c4c4', marginRight: '4px' }} />
              }
              fullWidth
              placeholder="Search"
              className={classes.toolbarSearch}
            />
          </Box>
          <Box
            display="flex"
            justifyContent="flex-end"
            className={classes.toolbarItemContainer}
          >
            <Box p={1}>
              <Button
                aria-label="Alert Metrics Sort"
                size={'small'}
                aria-describedby={sortMenuId}
                onClick={e => {
                  setSortMenuAnchorEl(e.currentTarget);
                }}
                startIcon={!isLessThanSm && <SortIcon />}
                className={classes.toolbarButton}
                style={{ marginRight: '7px' }}
                color="primary"
                variant="outlined"
              >
                {isLessThanSm ? <SortIcon /> : 'Sort'}
              </Button>
              <Menu
                id={sortMenuId}
                open={openSortMenu}
                anchorEl={sortMenuAnchorEl}
                onClose={e => {
                  setSortMenuAnchorEl(null);
                }}
                style={{ paddingRight: '10px' }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                keepMounted
              >
                <MenuItem
                  onClick={() => {
                    setOrderBy(StudentAlertOrderByTypes.OpenAlerts);
                    setSortMenuAnchorEl(null);
                  }}
                  className={classes.menuItem}
                >
                  <RadioImpersonatorIcon
                    checked={orderBy === StudentAlertOrderByTypes.OpenAlerts}
                  />

                  <Typography>Open Alerts</Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setOrderBy(StudentAlertOrderByTypes.TotalAlerts);
                    setSortMenuAnchorEl(null);
                  }}
                  className={classes.menuItem}
                >
                  <RadioImpersonatorIcon
                    checked={orderBy === StudentAlertOrderByTypes.TotalAlerts}
                  />
                  <Typography>Total Alerts</Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setOrderBy(undefined);
                    setSortMenuAnchorEl(null);
                  }}
                  className={classes.menuItem}
                >
                  <RadioImpersonatorIcon checked={orderBy === undefined} />
                  <Typography>Last Name</Typography>
                </MenuItem>
              </Menu>
              <Button
                arial-label="Filters"
                size={'small'}
                startIcon={!isLessThanSm && <TuneIcon />}
                onClick={e => {
                  setFilterMenuAnchorEl(e.currentTarget);
                }}
                color="primary"
                variant="outlined"
              >
                {isLessThanSm ? <TuneIcon /> : 'Filters'}
              </Button>
              <Popover
                open={openFilterMenu}
                onClose={() => setFilterMenuAnchorEl(null)}
                anchorEl={filterMenuAnchorEl}
                PaperProps={{
                  style: { padding: '15px', width: '400px' },
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
              >
                <StudentFilterPopoverContent
                  followingFilter={followingFilter}
                  termFilter={termFilter}
                  onApply={(f, t) => {
                    setFollowingFilter(f);
                    setTermFilter(t);
                  }}
                  onClose={() => setFilterMenuAnchorEl(null)}
                />
              </Popover>
            </Box>
          </Box>
        </Box>
      </Toolbar>

      {items.length === 0 && isExhausted ? (
        <NoItemBox
          height={height - 450}
          minHeight={575}
          text="No Students To Display"
        />
      ) : (
        <div
          style={{
            height: height - 450,
            minHeight: 575,
            overflowY: 'scroll',
            overflowX: 'hidden',
          }}
        >
          {initialLoad ? (
            <ul
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                position: 'relative',
              }}
            >
              {[...Array(PAGE_SIZE)].map((e, i) => {
                return <StudentsSkeleton key={i} />;
              })}
            </ul>
          ) : (
            <InfiniteScroll
              element="ul"
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                position: 'relative',
              }}
              useWindow={false}
              initialLoad={false}
              hasMore={!isExhausted && !isLoading}
              threshold={500}
              loadMore={() => {
                setIsLoading(true);
                getter()
                  .then(r => setItems([...items, ...r]))
                  .catch(e => {
                    enqueueSnackbar(
                      'Failed to load Students. Please refresh this page and try again later',
                      {
                        variant: 'error',
                      },
                    );
                  })
                  .finally(() => {
                    setIsLoading(false);
                  });
              }}
            >
              {items.map((e, i) => {
                return (
                  <StudentsListItem
                    key={i}
                    coreUser={e}
                    isCurrentTerm={termFilter === 'current'}
                    isFollowing={following.findIndex(u => u === e.id) > -1}
                    onFollowChange={unfollow => {
                      if (unfollow) {
                        setFollowing(f =>
                          f.splice(
                            f.findIndex(u => u === e.id),
                            1,
                          ),
                        );
                      } else {
                        setFollowing(f => f.concat([e.id]));
                      }
                    }}
                  />
                );
              })}
            </InfiniteScroll>
          )}
          {isLoading && <LinearProgress aria-busy={true} />}
        </div>
      )}
      {initialLoad ? (
        <MetricFooterSkeleton />
      ) : (
        <MetricFooter currentCount={items.length} totalCount={totalItems} />
      )}
    </Paper>
  );
};

export default Students;
